/**
 * AI Store
 *
 * Zustand store for AI assistance state management
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
  AIConfig,
  ChatMessage,
  EnhancedErrorEntry,
  SanityCheckResult,
} from './types';

interface AIState {
  // Configuration
  config: AIConfig;

  // Error explanations
  enhancedErrors: EnhancedErrorEntry[];
  selectedErrorId: string | null;

  // Chat
  chatMessages: ChatMessage[];
  isChatOpen: boolean;
  isChatLoading: boolean;

  // Sanity checks
  sanityCheckResults: SanityCheckResult[];
  isSanityCheckRunning: boolean;

  // Actions - Config
  setApiKey: (key: string | null) => void;
  setUseServerProxy: (use: boolean) => void;

  // Actions - Errors
  setEnhancedErrors: (errors: EnhancedErrorEntry[]) => void;
  updateErrorExplanation: (
    errorId: string,
    update: Partial<EnhancedErrorEntry>
  ) => void;
  selectError: (errorId: string | null) => void;
  clearErrors: () => void;

  // Actions - Chat
  addChatMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  updateLastAssistantMessage: (content: string, isStreaming: boolean) => void;
  clearChat: () => void;
  toggleChat: () => void;
  setChatLoading: (loading: boolean) => void;

  // Actions - Sanity Checks
  setSanityCheckResults: (results: SanityCheckResult[]) => void;
  setSanityCheckRunning: (running: boolean) => void;
  dismissSanityCheck: (id: string) => void;
}

export const useAIStore = create<AIState>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        config: {
          apiKey: null,
          useServerProxy: true,
          model: 'claude-sonnet-4-20250514',
          maxTokens: 2048,
        },

        enhancedErrors: [],
        selectedErrorId: null,

        chatMessages: [],
        isChatOpen: false,
        isChatLoading: false,

        sanityCheckResults: [],
        isSanityCheckRunning: false,

        // Config actions
        setApiKey: (key) => {
          set((state) => ({
            config: { ...state.config, apiKey: key },
          }));
        },

        setUseServerProxy: (use) => {
          set((state) => ({
            config: { ...state.config, useServerProxy: use },
          }));
        },

        // Error actions
        setEnhancedErrors: (errors) => {
          set({ enhancedErrors: errors });
        },

        updateErrorExplanation: (errorId, update) => {
          set((state) => ({
            enhancedErrors: state.enhancedErrors.map((error) =>
              error.id === errorId ? { ...error, ...update } : error
            ),
          }));
        },

        selectError: (errorId) => {
          set({ selectedErrorId: errorId });
        },

        clearErrors: () => {
          set({ enhancedErrors: [], selectedErrorId: null });
        },

        // Chat actions
        addChatMessage: (message) => {
          const newMessage: ChatMessage = {
            ...message,
            id: crypto.randomUUID(),
            timestamp: Date.now(),
          };
          set((state) => ({
            chatMessages: [...state.chatMessages, newMessage],
          }));
        },

        updateLastAssistantMessage: (content, isStreaming) => {
          set((state) => {
            const messages = [...state.chatMessages];
            const lastIndex = messages.length - 1;
            const lastMessage = messages[lastIndex];
            if (lastIndex >= 0 && lastMessage && lastMessage.role === 'assistant') {
              messages[lastIndex] = {
                id: lastMessage.id,
                role: lastMessage.role,
                timestamp: lastMessage.timestamp,
                content,
                isStreaming,
              };
            }
            return { chatMessages: messages };
          });
        },

        clearChat: () => {
          set({ chatMessages: [] });
        },

        toggleChat: () => {
          set((state) => ({ isChatOpen: !state.isChatOpen }));
        },

        setChatLoading: (loading) => {
          set({ isChatLoading: loading });
        },

        // Sanity check actions
        setSanityCheckResults: (results) => {
          set({ sanityCheckResults: results });
        },

        setSanityCheckRunning: (running) => {
          set({ isSanityCheckRunning: running });
        },

        dismissSanityCheck: (id) => {
          set((state) => ({
            sanityCheckResults: state.sanityCheckResults.filter(
              (r) => r.id !== id
            ),
          }));
        },
      }),
      {
        name: 'envelop-ai-storage',
        partialize: (state) => ({
          config: {
            apiKey: state.config.apiKey,
            useServerProxy: state.config.useServerProxy,
          },
        }),
      }
    ),
    { name: 'AIStore' }
  )
);

// Selectors
export const selectHasApiKey = (state: AIState) =>
  state.config.apiKey !== null || state.config.useServerProxy;

export const selectSelectedError = (state: AIState) =>
  state.enhancedErrors.find((e) => e.id === state.selectedErrorId) || null;

export const selectErrorsBySelection = (state: AIState) => ({
  selected: state.enhancedErrors.find((e) => e.id === state.selectedErrorId),
  others: state.enhancedErrors.filter((e) => e.id !== state.selectedErrorId),
});
